import { m } from "@mojo/i18n";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Bug, Lightbulb, Send, Sparkles } from "lucide-react";
import { useState } from "react";
import { PageHeader } from "@/components/layout";
import { Button, Card, Input, Textarea } from "@/components/ui";

export const Route = createFileRoute("/_authenticated/issues/new")({
	component: NewIssuePage,
});

type IssueType = "bug" | "improvement" | "feature";

interface IssueFormData {
	type: IssueType;
	title: string;
	description: string;
	stepsToReproduce?: string; // Bug only
	expectedBehavior?: string; // Bug only
	currentBehavior?: string; // Bug only
	useCase?: string; // Feature/Improvement
	severity?: "critical" | "major" | "minor" | "trivial"; // Bug only
	priority?: "high" | "medium" | "low"; // Feature/Improvement
}

function NewIssuePage() {
	const navigate = useNavigate();
	const [activeTab, setActiveTab] = useState<IssueType>("bug");
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [formData, setFormData] = useState<IssueFormData>({
		type: "bug",
		title: "",
		description: "",
		stepsToReproduce: "",
		expectedBehavior: "",
		currentBehavior: "",
		useCase: "",
		severity: "minor", // Default for bugs
		priority: "medium", // Default for features/improvements
	});

	const updateField = (field: keyof IssueFormData, value: string) => {
		setFormData((prev) => ({ ...prev, [field]: value }));
	};

	const handleTabChange = (tab: IssueType) => {
		setActiveTab(tab);
		setFormData((prev) => ({ ...prev, type: tab }));
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setIsSubmitting(true);

		try {
			// Submit to the API endpoint
			// Bugs use severity, features/improvements use priority
			const response = await fetch("/api/issues/submit", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({
					type: formData.type,
					title: formData.title,
					description: formData.description,
					...(formData.type === "bug"
						? { severity: formData.severity }
						: { priority: formData.priority }),
					fields:
						formData.type === "bug"
							? {
									stepsToReproduce: formData.stepsToReproduce,
									expectedBehavior: formData.expectedBehavior,
									currentBehavior: formData.currentBehavior,
								}
							: {
									useCase: formData.useCase,
								},
				}),
			});

			const result = await response.json();

			if (!result.success) {
				throw new Error(result.error || "Failed to submit issue");
			}

			// Navigate back to issues list
			navigate({ to: "/issues" });
		} catch (error) {
			console.error("Failed to submit issue:", error);
			// TODO: Show error toast to user
		} finally {
			setIsSubmitting(false);
		}
	};

	const isValid =
		formData.title.trim().length > 0 && formData.description.trim().length > 0;

	const tabs = [
		{
			id: "bug" as const,
			label: m.issues_tab_bug(),
			icon: <Bug className="h-4 w-4" />,
		},
		{
			id: "improvement" as const,
			label: m.issues_tab_improvement(),
			icon: <Sparkles className="h-4 w-4" />,
		},
		{
			id: "feature" as const,
			label: m.issues_tab_feature(),
			icon: <Lightbulb className="h-4 w-4" />,
		},
	];

	return (
		<div>
			<PageHeader
				title={m.issues_new_title()}
				description={m.issues_new_description()}
				actions={
					<a href="/issues">
						<Button variant="ghost" icon={<ArrowLeft className="h-4 w-4" />}>
							{m.issues_back()}
						</Button>
					</a>
				}
			/>

			<Card>
				{/* Tabs */}
				<div className="mb-6 flex border-border border-b">
					{tabs.map((tab) => (
						<button
							key={tab.id}
							type="button"
							onClick={() => handleTabChange(tab.id)}
							className={`flex items-center gap-2 border-b-2 px-4 py-3 font-medium text-sm transition-colors ${
								activeTab === tab.id
									? "border-accent text-accent"
									: "border-transparent text-text-secondary hover:text-text-primary"
							}`}
						>
							{tab.icon}
							{tab.label}
						</button>
					))}
				</div>

				{/* Form */}
				<form onSubmit={handleSubmit} className="space-y-6">
					{/* Title */}
					<div>
						<label
							htmlFor="title"
							className="mb-2 block font-medium text-sm text-text-secondary"
						>
							{m.issues_form_title()} <span className="text-bug">*</span>
						</label>
						<Input
							id="title"
							value={formData.title}
							onChange={(e) => updateField("title", e.target.value)}
							placeholder={
								activeTab === "bug"
									? m.issues_placeholder_bug_title()
									: activeTab === "improvement"
										? m.issues_placeholder_improvement_title()
										: m.issues_placeholder_feature_title()
							}
							required
						/>
					</div>

					{/* Description */}
					<div>
						<label
							htmlFor="description"
							className="mb-2 block font-medium text-sm text-text-secondary"
						>
							{m.issues_form_description()} <span className="text-bug">*</span>
						</label>
						<Textarea
							id="description"
							value={formData.description}
							onChange={(e) => updateField("description", e.target.value)}
							placeholder={
								activeTab === "bug"
									? m.issues_placeholder_bug_description()
									: activeTab === "improvement"
										? m.issues_placeholder_improvement_description()
										: m.issues_placeholder_feature_description()
							}
							rows={4}
							required
						/>
					</div>

					{/* Bug-specific fields */}
					{activeTab === "bug" && (
						<>
							<div>
								<label
									htmlFor="stepsToReproduce"
									className="mb-2 block font-medium text-sm text-text-secondary"
								>
									{m.issues_form_steps()}
								</label>
								<Textarea
									id="stepsToReproduce"
									value={formData.stepsToReproduce}
									onChange={(e) =>
										updateField("stepsToReproduce", e.target.value)
									}
									placeholder={m.issues_placeholder_steps()}
									rows={3}
								/>
							</div>

							<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
								<div>
									<label
										htmlFor="expectedBehavior"
										className="mb-2 block font-medium text-sm text-text-secondary"
									>
										{m.issues_form_expected()}
									</label>
									<Textarea
										id="expectedBehavior"
										value={formData.expectedBehavior}
										onChange={(e) =>
											updateField("expectedBehavior", e.target.value)
										}
										placeholder={m.issues_placeholder_expected()}
										rows={2}
									/>
								</div>
								<div>
									<label
										htmlFor="currentBehavior"
										className="mb-2 block font-medium text-sm text-text-secondary"
									>
										{m.issues_form_current()}
									</label>
									<Textarea
										id="currentBehavior"
										value={formData.currentBehavior}
										onChange={(e) =>
											updateField("currentBehavior", e.target.value)
										}
										placeholder={m.issues_placeholder_current()}
										rows={2}
									/>
								</div>
							</div>
						</>
					)}

					{/* Feature/Improvement specific fields */}
					{(activeTab === "feature" || activeTab === "improvement") && (
						<div>
							<label
								htmlFor="useCase"
								className="mb-2 block font-medium text-sm text-text-secondary"
							>
								{m.issues_form_usecase()}
							</label>
							<Textarea
								id="useCase"
								value={formData.useCase}
								onChange={(e) => updateField("useCase", e.target.value)}
								placeholder={m.issues_placeholder_usecase()}
								rows={3}
							/>
						</div>
					)}

					{/* Severity (for bugs) */}
					{activeTab === "bug" && (
						<fieldset>
							<legend className="mb-2 block font-medium text-sm text-text-secondary">
								{m.issues_form_severity()}
							</legend>
							<div className="flex flex-wrap gap-3">
								{(["critical", "major", "minor", "trivial"] as const).map(
									(severity) => (
										<button
											key={severity}
											type="button"
											onClick={() => updateField("severity", severity)}
											className={`flex flex-col items-start rounded-lg border px-4 py-2 text-left transition-colors ${
												formData.severity === severity
													? severity === "critical"
														? "border-bug bg-bug/20 text-bug"
														: severity === "major"
															? "border-orange-500 bg-orange-500/20 text-orange-500"
															: severity === "minor"
																? "border-yellow-500 bg-yellow-500/20 text-yellow-500"
																: "border-accent bg-accent/20 text-accent"
													: "border-border bg-bg-elevated text-text-secondary hover:text-text-primary"
											}`}
										>
											<span className="font-medium text-sm">
												{severity === "critical"
													? m.severity_critical()
													: severity === "major"
														? m.severity_major()
														: severity === "minor"
															? m.severity_minor()
															: m.severity_trivial()}
											</span>
											<span className="text-xs opacity-70">
												{severity === "critical"
													? m.severity_critical_desc()
													: severity === "major"
														? m.severity_major_desc()
														: severity === "minor"
															? m.severity_minor_desc()
															: m.severity_trivial_desc()}
											</span>
										</button>
									),
								)}
							</div>
						</fieldset>
					)}

					{/* Priority (for features/improvements) */}
					{(activeTab === "feature" || activeTab === "improvement") && (
						<fieldset>
							<legend className="mb-2 block font-medium text-sm text-text-secondary">
								{m.issues_form_priority()}
							</legend>
							<div className="flex flex-wrap gap-3">
								{(["high", "medium", "low"] as const).map((priority) => (
									<button
										key={priority}
										type="button"
										onClick={() => updateField("priority", priority)}
										className={`flex flex-col items-start rounded-lg border px-4 py-2 text-left transition-colors ${
											formData.priority === priority
												? priority === "high"
													? "border-bug bg-bug/20 text-bug"
													: priority === "medium"
														? "border-yellow-500 bg-yellow-500/20 text-yellow-500"
														: "border-accent bg-accent/20 text-accent"
												: "border-border bg-bg-elevated text-text-secondary hover:text-text-primary"
										}`}
									>
										<span className="font-medium text-sm">
											{priority === "high"
												? m.priority_high()
												: priority === "medium"
													? m.priority_medium()
													: m.priority_low()}
										</span>
										<span className="text-xs opacity-70">
											{priority === "high"
												? m.priority_high_desc()
												: priority === "medium"
													? m.priority_medium_desc()
													: m.priority_low_desc()}
										</span>
									</button>
								))}
							</div>
						</fieldset>
					)}

					{/* Submit */}
					<div className="flex justify-end gap-3 border-border border-t pt-4">
						<a href="/issues">
							<Button type="button" variant="ghost">
								{m.issues_cancel()}
							</Button>
						</a>
						<Button
							type="submit"
							icon={<Send className="h-4 w-4" />}
							loading={isSubmitting}
							disabled={!isValid}
						>
							{activeTab === "bug"
								? m.issues_submit_bug()
								: activeTab === "improvement"
									? m.issues_submit_improvement()
									: m.issues_submit_feature()}
						</Button>
					</div>
				</form>
			</Card>
		</div>
	);
}
